import { forwardRef } from 'react';

import Icon from './Icon';

/** Label + control + error, so every form in the console looks the same. */
export function Field({ label, hint, error, required, children, className = '' }) {
  return (
    <div className={`field ${className}`}>
      {label && (
        <label className="field-label">
          {label}
          {required && <span style={{ color: 'var(--danger-fg)' }}> *</span>}
        </label>
      )}
      {hint && <div className="field-hint">{hint}</div>}
      {children}
      {error && (
        <div className="field-error">
          <Icon name="error" style={{ fontSize: 13, verticalAlign: '-2px', marginRight: 4 }} />
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * The form controls. Two things about them are load-bearing.
 *
 * 1. They MUST forward their ref. react-hook-form's `register()` returns a ref
 *    it attaches to the real DOM node, and that node is how it both reads and
 *    WRITES values. A plain function component silently swallows the ref, so
 *    `reset()` has nothing to write into and every edit form opens blank.
 *
 * 2. Browser autofill is off by default. This is an admin console: a field
 *    called `name` means a category or business name, never the operator's own.
 *    Chrome fills such fields from its saved profile anyway, without firing an
 *    input event, so the form would believe them empty and block the submit.
 *    The credential forms opt back in by passing autoComplete explicitly.
 */
export const TextInput = forwardRef(function TextInput(
  { error, autoComplete = 'off', ...props },
  ref
) {
  return (
    <input
      ref={ref}
      className={`field-control ${error ? 'is-invalid' : ''}`}
      autoComplete={autoComplete}
      {...props}
    />
  );
});

export const TextArea = forwardRef(function TextArea(
  { error, autoComplete = 'off', ...props },
  ref
) {
  return (
    <textarea
      ref={ref}
      className={`field-control ${error ? 'is-invalid' : ''}`}
      autoComplete={autoComplete}
      {...props}
    />
  );
});

export const Select = forwardRef(function Select(
  { error, options = [], placeholder, ...props },
  ref
) {
  return (
    <select ref={ref} className={`field-control ${error ? 'is-invalid' : ''}`} {...props}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => {
        const value = typeof o === 'string' ? o : o.value ?? o.id;
        const label = typeof o === 'string' ? o : o.label ?? o.name;
        return (
          <option key={value} value={value}>
            {label}
          </option>
        );
      })}
    </select>
  );
});

/** Multi-select rendered as toggleable chips. */
export function ChipSelect({ options = [], value = [], onChange }) {
  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  return (
    <div className="chip-row">
      {options.map((o) => {
        const id = o.id ?? o.value;
        const label = o.name ?? o.label;
        const on = value.includes(id);
        return (
          <button
            key={id}
            type="button"
            className={`chip ${on ? 'is-on' : ''}`}
            onClick={() => toggle(id)}
            aria-pressed={on}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function RadioGroup({ options = [], value, onChange, name }) {
  return (
    <div className="radio-row" role="radiogroup" aria-label={name}>
      {options.map((o) => {
        const val = typeof o === 'string' ? o : o.value;
        const label = typeof o === 'string' ? o : o.label;
        const on = value === val;
        return (
          <button
            key={val}
            type="button"
            className={`radio-card ${on ? 'is-on' : ''}`}
            onClick={() => onChange(val)}
            role="radio"
            aria-checked={on}
          >
            <span className="radio-dot" />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function Toggle({ checked, onChange, label, hint, id }) {
  return (
    <div className="toggle-row">
      <div>
        <div className="field-label" style={{ marginBottom: 2 }}>
          {label}
        </div>
        {hint && <div className="field-hint">{hint}</div>}
      </div>
      <button
        type="button"
        id={id}
        className={`toggle ${checked ? 'is-on' : ''}`}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        aria-label={label}
      >
        <span className="toggle-knob" />
      </button>
    </div>
  );
}
